import { useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faFileLines,
  faMessageLines,
} from "@fortawesome/pro-regular-svg-icons";
import feedbackRequestApi from "../../../api/feedback/feedbackRequestApi";
import {
  FeedbackRequestDto,
  FeedbackMode,
} from "../../../types/feedback/FeedbackRequestDto";
import {
  normalizeFeedbackMode,
  normalizeFeedbackRequestStatus,
} from "../../../helpers/feedbackModeHelper";
import { FormattedDate } from "../../common";
import Toast from "../../common/Toast";
import { ToastConfig } from "../../common/ToastConfig";
import AppRoutes from "../../../routes/AppRoutes";
import SmallLoader from "../../loaders/SmallLoader";
import { useTranslation } from "react-i18next";
import DashboardLeaveFeedbackModal from "./DashboardLeaveFeedbackModal";
import { formatPendingRequestSummary } from "./shared/formatPendingRequestSummary";
import FeedbackRequestMessageQuote from "./shared/FeedbackRequestMessageQuote";

function requestModeIcon(mode: FeedbackMode) {
  if (normalizeFeedbackMode(mode) === "FULLREVIEWACCESS") {
    return faFileLines;
  }
  return faMessageLines;
}

function PendingFeedbackWidget() {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<FeedbackRequestDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [leaveFeedbackRequest, setLeaveFeedbackRequest] =
    useState<FeedbackRequestDto | null>(null);
  const [toastConfig, setToastConfig] = useState<ToastConfig>(
    ToastConfig.hidden(),
  );

  const api = useMemo(
    () => new feedbackRequestApi(auth.user?.access_token),
    [auth.user?.access_token],
  );

  const loadPendingRequests = () => {
    setHasError(false);
    api.getPendingRequests(
      (data) => {
        setRequests(
          data.filter(
            (r) => normalizeFeedbackRequestStatus(r.status) === "PENDING",
          ),
        );
        setIsLoading(false);
      },
      () => {
        setHasError(true);
        setIsLoading(false);
      },
    );
  };

  useEffect(() => {
    loadPendingRequests();
  }, []);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <SmallLoader />
      </div>
    );
  }

  if (hasError || (requests.length === 0 && leaveFeedbackRequest === null)) {
    return null;
  }

  const handleViewDoc = (request: FeedbackRequestDto) => {
    if (!request.answerSetUniqueId) return;
    const route = AppRoutes.collaborativeDocument.generateRoute(
      request.answerSetUniqueId,
      false,
      null,
    );
    navigate(route);
  };

  const widgetTitle = t("Feedback.Dashboard.PendingSectionTitle", {
    defaultValue: "Feedback Requests",
  });

  const leaveModalOpen = leaveFeedbackRequest !== null;

  return (
    <>
      {requests.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-4">
          <button
            type="button"
            className="w-full px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2 text-left hover:bg-gray-50/80 rounded-t-lg"
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
          >
            <span className="text-sm font-semibold text-gray-800 flex items-center gap-2 flex-wrap">
              {widgetTitle}
              <span
                className="inline-flex min-h-[1.25rem] min-w-[1.25rem] items-center justify-center rounded-full bg-primary/10 px-2 text-xs font-semibold tabular-nums text-primary"
                aria-label={t("Feedback.Dashboard.PendingCountLabel", {
                  count: requests.length,
                  defaultValue: "{{count}} pending",
                })}
              >
                {requests.length}
              </span>
            </span>
            <FontAwesomeIcon
              icon={expanded ? faChevronDown : faChevronRight}
              className="text-gray-400 flex-shrink-0"
            />
          </button>
          {expanded && (
            <div className="divide-y divide-gray-100">
              {requests.map((request) => (
                <div key={request.id} className="px-4 py-2.5">
                  <div className="flex items-start gap-2.5">
                    <div
                      className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
                      aria-hidden
                    >
                      <FontAwesomeIcon
                        icon={requestModeIcon(request.modeAtCreation)}
                        className="text-sm"
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-800 leading-snug">
                          {formatPendingRequestSummary(request, t)}
                        </p>
                        {request.message?.trim() && (
                          <FeedbackRequestMessageQuote
                            message={request.message}
                            ariaLabelKey="Feedback.Dashboard.RequestMessageAria"
                            className="mt-1 rounded-r-md border-l-2 border-l-gray-300 bg-gray-50/90 px-3 py-2 text-sm text-gray-500 italic whitespace-pre-wrap break-words"
                          />
                        )}
                        <span className="text-xs text-gray-400 mt-0.5 block">
                          <FormattedDate
                            date={new Date(request.dateCreated)}
                            displayMode="FROM-NOW"
                            convertFromUtc
                          />
                        </span>
                      </div>
                      <div className="flex-shrink-0 sm:pt-0.5">
                        {normalizeFeedbackMode(request.modeAtCreation) ===
                        "FULLREVIEWACCESS" ? (
                          <button
                            type="button"
                            className="btn-primary text-sm !px-3 py-1.5 whitespace-nowrap shrink-0"
                            onClick={() => handleViewDoc(request)}
                            disabled={!request.answerSetUniqueId}
                          >
                            {t("Feedback.Dashboard.CtaFullAccess", {
                              defaultValue: "Give feedback",
                            })}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn-primary text-sm !px-3 py-1.5 whitespace-nowrap shrink-0"
                            onClick={() => setLeaveFeedbackRequest(request)}
                          >
                            {t("Feedback.Dashboard.CtaLeaveFeedback", {
                              defaultValue: "Give feedback",
                            })}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <DashboardLeaveFeedbackModal
        isOpen={leaveModalOpen}
        request={leaveFeedbackRequest}
        api={api}
        onClose={() => {
          setLeaveFeedbackRequest(null);
        }}
        onRequestCompleted={() => {
          loadPendingRequests();
        }}
        onSaveError={(title, message) => {
          setToastConfig(ToastConfig.error(title, message));
        }}
      />

      {toastConfig.show && (
        <Toast
          title={toastConfig.title}
          message={toastConfig.message}
          type={toastConfig.type}
          onClose={() => setToastConfig(ToastConfig.hidden())}
        />
      )}
    </>
  );
}

export default PendingFeedbackWidget;
